// File: frontend/src/components/Settings/ConfigurationExport.jsx
// Configuration export/import component

import { useState } from 'react';
import { useMutation } from 'react-query';
import { 
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { exportConfiguration, importConfiguration } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';

const ConfigurationExport = () => {
  const [notification, setNotification] = useState(null);
  
  // Export mutation
  const exportMutation = useMutation({
    mutationFn: exportConfiguration,
    onSuccess: (data) => {
      // Create and download file
      const blob = new Blob([JSON.stringify(data.data, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-config-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showNotification('success', 'Configuración exportada exitosamente');
    },
    onError: (error) => {
      showNotification('error', error.response?.data?.message || 'Error al exportar configuración');
    }
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: importConfiguration,
    onSuccess: () => {
      showNotification('success', 'Configuración importada exitosamente');
    },
    onError: (error) => {
      showNotification('error', error.response?.data?.message || 'Error al importar configuración');
    }
  });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleExport = () => {
    exportMutation.mutate();
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const config = JSON.parse(e.target.result);
        importMutation.mutate(config);
      } catch (error) {
        showNotification('error', 'Archivo de configuración inválido');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="p-6">
      {/* Notification */}
      {notification && (
        <div className={`mb-6 rounded-md p-4 ${
          notification.type === 'success' ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className="flex">
            {notification.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            )}
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Export Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <DocumentArrowDownIcon className="h-6 w-6 text-indigo-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Exportar Configuración</h3>
              <p className="text-sm text-gray-500">
                Descarga todas las configuraciones del sistema en un archivo JSON.
              </p>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-md p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">La exportación incluye:</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Configuraciones del sistema</li>
              <li>• Etapas del pipeline de ventas</li>
              <li>• Configuraciones de notificaciones</li>
              <li>• Configuraciones de seguridad</li>
            </ul>
          </div>
          
          <button
            onClick={handleExport}
            disabled={exportMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {exportMutation.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            )}
            Exportar Configuración
          </button>
        </div>

        {/* Import Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <DocumentArrowUpIcon className="h-6 w-6 text-orange-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Importar Configuración</h3>
              <p className="text-sm text-gray-500">
                Carga configuraciones desde un archivo JSON exportado previamente.
              </p>
            </div>
          </div>
          
          <div className="bg-yellow-50 rounded-md p-4 mb-4">
            <div className="flex">
              <ExclamationCircleIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">Advertencia</h4>
                <div className="mt-1 text-sm text-yellow-700">
                  <p>
                    Esta función está en desarrollo. La importación de configuraciones 
                    podría sobrescribir las configuraciones actuales.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <label className="relative cursor-pointer">
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                disabled={importMutation.isPending}
                className="sr-only"
              />
              <span className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                {importMutation.isPending ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <DocumentArrowUpIcon className="h-4 w-4 mr-2" />
                )}
                Seleccionar Archivo
              </span>
            </label>
          </div>
        </div>

        {/* Backup Recommendations */}
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex">
            <CheckCircleIcon className="h-6 w-6 text-blue-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-medium text-blue-900">Recomendaciones de Respaldo</h3>
              <div className="mt-2 text-sm text-blue-800 space-y-2">
                <p>• Exporta las configuraciones regularmente como respaldo.</p>
                <p>• Guarda los archivos de exportación en un lugar seguro.</p>
                <p>• Prueba la importación en un entorno de desarrollo antes de usarla en producción.</p>
                <p>• Documenta los cambios importantes en las configuraciones.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationExport;